import axios from "axios";
// import Unauthorized from "../pages/Errors/Error403";
// import NotFound from "../pages/Errors/Error404";
// import NetworkError from "../pages/Errors/Error500";
// import BadRequest from "../pages/Errors/Error400";

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const API = axios.create({
  withCredentials: false,
  timeout: 30000000, // More reasonable timeout value
  headers: {
    "ngrok-skip-browser-warning": "true"
  }
});

API.interceptors.request.use((config) => {
  const skipAuth = config.headers['X-Skip-Auth'];
  if (skipAuth) {
    delete config.headers['X-Skip-Auth'];
    return config;
  }

  const authToken = localStorage.getItem("token");
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

const clearIndexedDB = async () => {
  if (typeof window === "undefined" || !window.indexedDB) return;

  const deleteDatabase = (name) =>
    new Promise((resolve) => {
      if (!name) {
        resolve();
        return;
      }
      const deleteRequest = window.indexedDB.deleteDatabase(name);
      deleteRequest.onsuccess = () => resolve();
      deleteRequest.onerror = () => resolve();
      deleteRequest.onblocked = () => resolve();
    });

  try {
    if (window.indexedDB.databases) {
      const databases = await window.indexedDB.databases();
      await Promise.all(databases.map((db) => deleteDatabase(db.name)));
    }
  } catch (error) {
    console.error("Failed to clear IndexedDB:", error);
  }
};

const handleUnauthorized = () => {
  if (window.__inboxlogyLoggingOut) {
    return;
  }
  window.__inboxlogyLoggingOut = true;

  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch (storageError) {
    console.error("Failed to clear storage during logout:", storageError);
  }

  clearIndexedDB().catch((dbError) => {
    console.error("Failed to clear IndexedDB during logout:", dbError);
  });

  const loginUrl = `${import.meta.env.API_URL || ""}/`;

  if (window.location.pathname !== loginUrl) {
    window.location.replace(loginUrl);
  }
};

API.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response ? error.response.status : null;
    const data = error.response ? error.response.data : null;

    // Use backend-provided message if available
    const backendMessage = data && (typeof data === 'object') ? (data.message || data.error) : null;

    if (status === 401 || status === 403) {
      // Handle unauthorized access
      handleUnauthorized();
      throw new ApiError(backendMessage || "The user session has expired.", status);
    }

    if (backendMessage) {
      throw new ApiError(backendMessage, status);
    }

    if (status === 400) {
      // Bad request error
      throw new ApiError("A bad request occurred.", 400);
    } else if (status === 404) {
      // Handle not found errors
      throw new ApiError("The requested resource was not found.", 404);
    } else if (status === 502) {
      // Handle 502 Bad Gateway error
      throw new ApiError("502 Bad Gateway Error.", 502);
    } else if (status >= 500) {
      // Handle other server errors
      throw new ApiError("An unexpected server error occurred.", status);
    } else if (error.message && error.message.includes('timeout')) {
      // Handle timeout errors
      throw new ApiError("The request timed out. Please try again.", status);
    } else if (error.message && error.message.includes('Network Error')) {
      // Handle network errors
      throw new ApiError("A network error occurred. Please check your connection.", status);
    }

    // Do not wrap or log cancellation errors
    if (axios.isCancel(error) || error.name === 'AbortError' || error.message === 'canceled') {
      throw error;
    }

    // Handle unexpected errors
    throw new Error(error.message || "An unexpected error occurred.");
  }
);


export default API;
